import React from 'react';
import {
  Box,
  Heading,
  Text,
  Badge,
  Flex,
  Code,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';

const methodColors = {
  GET: 'green',
  POST: 'blue',
  PUT: 'orange',
  DELETE: 'red',
};

const ApiDocs = () => {
    const notificationEmailEndpoints = [
      {
        method: 'GET',
        path: '/api/v1/notification-emails/',
        description: 'List all notification email addresses.',
        response: '[\n  { "id": 1, "email": "admin@example.com", "created_at": "2024-01-01T00:00:00Z", "updated_at": "2024-01-01T00:00:00Z" }\n]'
      },
      {
        method: 'POST',
        path: '/api/v1/notification-emails/',
        description: 'Add a new notification email address.',
        request: '{ "email": "user@example.com" }',
        response: '{ "id": 2, "email": "user@example.com" }'
      },
      {
        method: 'PUT',
        path: '/api/v1/notification-emails/<id>/',
        description: 'Update an existing notification email address.',
        request: '{ "email": "new@example.com" }',
        response: '{ "id": 2, "email": "new@example.com" }'
      },
      {
        method: 'DELETE',
        path: '/api/v1/notification-emails/<id>/',
        description: 'Delete a notification email address.',
        response: '{ "deleted": true }'
      }
    ];
  const monitorEndpoints = [
    { 
      title: 'List all monitors', 
      method: 'GET', 
      path: '/api/v1/monitors/', 
      description: 'Retrieve a list of all configured uptime monitors.',
      response: `[
  {
    "id": 1,
    "url": "https://api.example.com/health",
    "description": "API Health Check",
    "status": true,
    "last_status": 200,
    "last_response_time": 0.234,
    "created_at": "2024-01-01T00:00:00Z",
    "updated_at": "2024-01-15T10:00:00Z"
  }
]`
    },
    { 
      title: 'Get monitor details', 
      method: 'GET', 
      path: '/api/v1/monitors/:id/', 
      description: 'Retrieve details for a specific monitor by ID.',
      response: `{
  "id": 1,
  "url": "https://api.example.com/health",
  "description": "API Health Check",
  "status": true,
  "last_status": 200,
  "last_response_time": 0.234,
  "created_at": "2024-01-01T00:00:00Z",
  "updated_at": "2024-01-15T10:00:00Z"
}`
    },
    { 
      title: 'Create monitor', 
      method: 'POST', 
      path: '/api/v1/monitors/', 
      description: 'Create a new uptime monitor.',
      request: `{
  "url": "https://api.example.com/health",
  "description": "API Health Check"
}`,
      response: `{
  "id": 2,
  "url": "https://api.example.com/health",
  "description": "API Health Check",
  "status": null,
  "last_status": null,
  "last_response_time": null,
  "created_at": "2024-01-16T00:00:00Z",
  "updated_at": "2024-01-16T00:00:00Z"
}`
    },
    { 
      title: 'Update monitor', 
      method: 'PUT', 
      path: '/api/v1/monitors/:id/', 
      description: 'Update an existing monitor by ID.',
      request: `{
  "url": "https://api.example.com/v2/health",
  "description": "Updated description"
}`,
      response: `{
  "id": 1,
  "url": "https://api.example.com/v2/health",
  "description": "Updated description",
  ...
}`
    },
    { 
      title: 'Delete monitor', 
      method: 'DELETE', 
      path: '/api/v1/monitors/:id/', 
      description: 'Delete a monitor and all its history.',
      response: `204 No Content`
    },
  ];

  const historyEndpoints = [
    { 
      title: 'Get monitor history', 
      method: 'GET', 
      path: '/api/v1/monitors/:id/history/', 
      description: 'Retrieve check history for a specific monitor.',
      response: `[
  {
    "id": 1,
    "monitor_id": 1,
    "status": 200,
    "response_time": 0.234,
    "created_at": "2024-01-15T10:00:00Z"
  },
  {
    "id": 2,
    "monitor_id": 1,
    "status": 200,
    "response_time": 0.189,
    "created_at": "2024-01-15T09:55:00Z"
  }
]`
    },
  ];

  const adminEndpoints = [
    { 
      title: 'List admin users', 
      method: 'GET', 
      path: '/api/v1/admin-users/', 
      description: 'Retrieve all admin users.',
      response: `[
  {
    "id": 1,
    "username": "admin",
    "email": "admin@example.com",
    "is_superuser": true,
    "is_active": true
  }
]`
    },
    { 
      title: 'Create admin user', 
      method: 'POST', 
      path: '/api/v1/admin-users/', 
      description: 'Create a new admin user.',
      request: `{
  "username": "newadmin",
  "email": "newadmin@example.com",
  "password": "securepassword",
  "is_superuser": false
}`,
      response: `{
  "id": 2,
  "username": "newadmin",
  "email": "newadmin@example.com",
  "is_superuser": false,
  "is_active": true
}`
    },
    { 
      title: 'Update admin user', 
      method: 'PUT', 
      path: '/api/v1/admin-users/:id/', 
      description: 'Update an existing admin user.',
      request: `{
  "email": "updated@example.com",
  "is_active": false
}`,
      response: `{
  "id": 1,
  "username": "admin",
  "email": "updated@example.com",
  "is_superuser": true,
  "is_active": false
}`
    },
    { 
      title: 'Delete admin user', 
      method: 'DELETE', 
      path: '/api/v1/admin-users/:id/', 
      description: 'Delete an admin user.',
      response: `{"success": true}`
    },
    { 
      title: 'Get current user', 
      method: 'GET', 
      path: '/api/v1/admin-users/me/', 
      description: 'Get the currently authenticated user.',
      response: `{
  "id": 1,
  "username": "admin",
  "email": "admin@example.com",
  "is_superuser": true,
  "is_active": true
}`
    },
  ];

  const settingsEndpoints = [
    { 
      title: 'Get global settings', 
      method: 'GET', 
      path: '/api/v1/global-settings/', 
      description: 'Retrieve global settings including IP allowlist/denylist.',
      response: `{
  "id": 1,
  "ip_whitelist": "192.168.1.1, 10.0.0.0/24",
  "ip_denylist": "1.2.3.4",
  "updated_at": "2024-01-15T10:00:00Z"
}`
    },
    { 
      title: 'Update global settings', 
      method: 'PUT', 
      path: '/api/v1/global-settings/1/', 
      description: 'Update global settings.',
      request: `{
  "ip_whitelist": "192.168.1.1, 10.0.0.0/24",
  "ip_denylist": "1.2.3.4, 5.6.7.8"
}`,
      response: `{
  "id": 1,
  "ip_whitelist": "192.168.1.1, 10.0.0.0/24",
  "ip_denylist": "1.2.3.4, 5.6.7.8",
  "updated_at": "2024-01-16T00:00:00Z"
}`
    },
  ];

  const legacyEndpoints = [
    { 
      title: 'Ping', 
      method: 'GET', 
      path: '/api/v1/ping', 
      description: 'Health check endpoint.',
      response: `{"success": true}`
    },
  ];

  const renderEndpoint = (endpoint) => (
    <AccordionItem key={endpoint.path + endpoint.method} border="none" mb={2}>
      <AccordionButton 
        bg="gray.50" 
        borderRadius="md" 
        _hover={{ bg: 'gray.100' }}
        _expanded={{ bg: 'gray.100', borderBottomRadius: 0 }}
      >
        <Flex flex="1" textAlign="left" align="center">
          <Badge colorScheme={methodColors[endpoint.method]} mr={3} fontSize="xs" px={2}>
            {endpoint.method}
          </Badge>
          <Code fontSize="sm" bg="transparent">{endpoint.path}</Code>
          <Text ml={4} color="gray.600" fontSize="sm">{endpoint.title}</Text>
        </Flex>
        <AccordionIcon />
      </AccordionButton>
      <AccordionPanel pb={4} bg="white" borderWidth="1px" borderTop="0" borderBottomRadius="md">
        <Text color="gray.600" mb={4}>{endpoint.description}</Text>
        
        {endpoint.request && (
          <Box mb={4}>
            <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={2}>Request Body:</Text>
            <Code display="block" p={4} borderRadius="md" fontSize="sm" whiteSpace="pre-wrap" bg="gray.50">
              {endpoint.request}
            </Code>
          </Box>
        )}
        
        <Box>
          <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={2}>Response:</Text>
          <Code display="block" p={4} borderRadius="md" fontSize="sm" whiteSpace="pre-wrap" bg="gray.50">
            {endpoint.response}
          </Code>
        </Box>
      </AccordionPanel>
    </AccordionItem>
  );

  return (
    <>
      <Box>
        <Heading size="lg" mb={2}>API Documentation</Heading>
        <Text color="gray.600" mb={6}>
          Reference documentation for the Uptime Monitor API. All endpoints require authentication via Token header.
        </Text>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
          <Heading size="md" mb={4}>Authentication</Heading>
          <Text color="gray.600" mb={4}>
            All API requests require authentication. Include your token in the Authorization header:
          </Text>
          <Code display="block" p={4} borderRadius="md" fontSize="sm" bg="gray.50">
            Authorization: Token your-token-here
          </Code>
          <Divider my={4} />
          <Heading size="sm" mb={2}>Login</Heading>
          <Text color="gray.600" mb={2}>
            To obtain a token, POST to the login endpoint:
          </Text>
          <Code display="block" p={4} borderRadius="md" fontSize="sm" bg="gray.50" mb={2}>
{`POST /api/v1/auth/login/
Content-Type: application/json

{
  "username": "your-username",
  "password": "your-password"
}`}
          </Code>
          <Text color="gray.600" fontSize="sm">
            Response: <Code fontSize="sm">{"{ \"token\": \"your-auth-token\" }"}</Code>
          </Text>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
          <Heading size="md" mb={4}>Monitors</Heading>
          <Text color="gray.600" mb={4}>
            Manage uptime monitors. Each monitor tracks the availability of a URL endpoint.
          </Text>
          <Accordion allowMultiple>
            {monitorEndpoints.map(renderEndpoint)}
          </Accordion>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
          <Heading size="md" mb={4}>Monitor History</Heading>
          <Text color="gray.600" mb={4}>
            Access historical check data for monitors.
          </Text>
          <Accordion allowMultiple>
            {historyEndpoints.map(renderEndpoint)}
          </Accordion>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
          <Heading size="md" mb={4}>Admin Users</Heading>
          <Text color="gray.600" mb={4}>
            Manage admin users for the Uptime Monitor dashboard.
          </Text>
          <Accordion allowMultiple>
            {adminEndpoints.map(renderEndpoint)}
          </Accordion>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
          <Heading size="md" mb={4}>Notification Emails API</Heading>
          <Text color="gray.600" mb={4}>
            Manage the list of email addresses that receive uptime alert notifications.
          </Text>
          <Accordion allowMultiple mb={4}>
            {notificationEmailEndpoints.map((endpoint) => (
              <AccordionItem key={endpoint.method + endpoint.path}>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <Badge colorScheme={methodColors[endpoint.method]} mr={2}>{endpoint.method}</Badge>
                    {endpoint.path}
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                  <Text mb={2}>{endpoint.description}</Text>
                  {endpoint.request && (
                    <>
                      <Text fontSize="sm" mb={1}>Request body:</Text>
                      <Code display="block" p={2} mb={2} borderRadius="md" fontSize="sm" whiteSpace="pre-wrap" bg="gray.50">{endpoint.request}</Code>
                    </>
                  )}
                  <Text fontSize="sm" mb={1}>Response:</Text>
                  <Code display="block" p={2} borderRadius="md" fontSize="sm" whiteSpace="pre-wrap" bg="gray.50">{endpoint.response}</Code>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
          <Accordion allowMultiple>
            {settingsEndpoints.map(renderEndpoint)}
          </Accordion>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
          <Heading size="md" mb={4}>Legacy API (v1)</Heading>
          <Text color="gray.600" mb={4}>
            These endpoints are maintained for backward compatibility.
          </Text>
          <Accordion allowMultiple>
            {legacyEndpoints.map(renderEndpoint)}
          </Accordion>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="md" mb={4}>Webhooks & Notifications</Heading>
          <Text color="gray.600" mb={4}>
            The Uptime Monitor can send notifications when monitors go down or recover.
            Configure webhooks and email notifications in the admin settings.
          </Text>
          <Box mb={4}>
            <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={2}>Webhook Payload (on status change):</Text>
            <Code display="block" p={4} borderRadius="md" fontSize="sm" whiteSpace="pre-wrap" bg="gray.50">
{`{
  "event": "monitor.status_change",
  "monitor": {
    "id": 1,
    "url": "https://api.example.com/health",
    "description": "API Health Check"
  },
  "previous_status": true,
  "current_status": false,
  "status_code": 503,
  "response_time": 5.234,
  "timestamp": "2024-01-15T10:00:00Z"
}`}
            </Code>
          </Box>
          <Box mt={4} p={4} bg="yellow.50" borderRadius="md" borderWidth="1px" borderColor="yellow.200">
            <Text fontSize="sm" color="yellow.800">
              <strong>Note:</strong> Webhook endpoints should respond with a 2xx status code within 30 seconds.
              Failed webhook deliveries will be retried up to 3 times with exponential backoff.
            </Text>
          </Box>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="md" mb={4}>PostgreSQL Status</Heading>
          <Text color="gray.600" mb={4}>
            Monitor PostgreSQL database health, table sizes, and active queries.
          </Text>
          <Accordion allowMultiple>
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <Badge colorScheme="green" mr={2}>GET</Badge>
                  /api/postgres/status/
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel>
                <Text mb={2}>Get server version, uptime, database size, connection stats and configuration.</Text>
                <Text fontSize="sm" mb={1}>Response:</Text>
                <Code display="block" p={2} borderRadius="md" fontSize="sm" whiteSpace="pre-wrap" bg="gray.50">
                  {'{ "success": true, "postgres": { "connected": true, "server_info": {...}, "database": {...}, "connections": {...} } }'}
                </Code>
              </AccordionPanel>
            </AccordionItem>
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <Badge colorScheme="green" mr={2}>GET</Badge>
                  /api/postgres/tables/?page=1&sort=size&search=
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel>
                <Text mb={2}>List tables with sizes, row counts, and vacuum stats. Supports pagination, sorting, and search.</Text>
                <Text fontSize="sm" mb={1}>Response:</Text>
                <Code display="block" p={2} borderRadius="md" fontSize="sm" whiteSpace="pre-wrap" bg="gray.50">
                  {'{ "success": true, "tables": [...], "pagination": { "page": 1, "pages": 1, "total": 10 } }'}
                </Code>
              </AccordionPanel>
            </AccordionItem>
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <Badge colorScheme="green" mr={2}>GET</Badge>
                  /api/postgres/activity/
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel>
                <Text mb={2}>Get active queries and connections from pg_stat_activity.</Text>
                <Text fontSize="sm" mb={1}>Response:</Text>
                <Code display="block" p={2} borderRadius="md" fontSize="sm" whiteSpace="pre-wrap" bg="gray.50">
                  {'{ "success": true, "activity": [{ "pid": 123, "state": "active", "query": "SELECT ..." }] }'}
                </Code>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>
      </Box>
    </>
  );
};

export default ApiDocs;
